/**
 * Main Application Component
 */

import { useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { useAuthStore } from '@store/authStore';
import { authApi } from '@api/auth';
import { ThemeProvider } from '@contexts/ThemeContext';
import { ToastProvider, useToast, setGlobalToast } from '@components/Toast';
import MainLayout from '@layouts/MainLayout';
import SettingsLayout from '@layouts/SettingsLayout';
import {
  AccessDeniedPage,
  DashboardPage,
  TasksPage,
  TemplatesPage,
  CalendarPage,
  ProfilePage,
  FilesPage,
  AppearanceSettingsPage,
  CategoriesSettingsPage,
  TagsSettingsPage,
  UsersSettingsPage,
  OdooImportPage,
  AdminSettingsPage,
  BusinessDomainsSettingsPage,
  AISettingsPage,
  ActivityLogPage,
  StatusSettingsPage,
  KanbanPage,
  SearchPage,
  AdminPanel,
  GlobalUsersPage,
  AccountabilityPage,
} from '@pages/index';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000, // 30 seconds
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function FullPageLoader() {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'var(--layout-content-bg, #f8f9fa)',
      }}
    >
      <Spin size="large" tip="Loading..." />
    </div>
  );
}

// Auth initializer component
function AuthInitializer({ children }: { children: React.ReactNode }) {
  const { isLoading, notProvisioned, setSession, setNotProvisioned, setLoading } = useAuthStore();
  const sessionChecked = useRef(false);
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  useEffect(() => {
    if (sessionChecked.current) return;
    sessionChecked.current = true;

    let cancelled = false;

    async function bootstrapSession() {
      setLoading(true);
      try {
        const session = await authApi.getMe();
        if (cancelled) return;
        setSession(session.user, session.currentTenant, session.permissions);
        if (session.currentTenant) {
          localStorage.setItem('bizops-tenant', session.currentTenant.tenantId);
        }
      } catch (error: unknown) {
        if (cancelled) return;
        const axiosError = error as { response?: { status?: number; data?: { code?: string } } };
        if (axiosError.response?.status === 403 && axiosError.response.data?.code === 'not_provisioned') {
          setNotProvisioned(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setIsBootstrapping(false);
        }
      }
    }

    bootstrapSession();

    return () => {
      cancelled = true;
    };
  }, [setLoading, setNotProvisioned, setSession]);

  if (isBootstrapping || isLoading) return <FullPageLoader />;
  if (notProvisioned) return <AccessDeniedPage />;

  return <>{children}</>;
}

function NotFoundPage() {
  return (
    <div style={{ padding: '100px', textAlign: 'center' }}>
      <h1>404 - Not Found</h1>
      <p>The page you're looking for doesn't exist.</p>
    </div>
  );
}

// Bridge component to set up global toast for non-React contexts
function ToastBridge({ children }: { children: React.ReactNode }) {
  const toast = useToast();
  useEffect(() => {
    setGlobalToast(toast);
  }, [toast]);
  return <>{children}</>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="blueprint" defaultFont="ibm-plex">
        <ToastProvider>
          <ToastBridge>
            <AuthInitializer>
              <Routes>
            {/* Standalone Admin Panel - verifies super admin from DB */}
            <Route
              path="/admin"
              element={<AdminPanel />}
            />

            {/* App routes with main layout */}
            <Route
              element={<MainLayout />}
            >
              <Route path="/" element={<DashboardPage />} />
              <Route path="/tasks" element={<TasksPage />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/files" element={<FilesPage />} />
              <Route path="/kanban" element={<KanbanPage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/accountability" element={<AccountabilityPage />} />

              {/* Settings routes with sidebar layout */}
              <Route path="/settings" element={<SettingsLayout />}>
                <Route index element={<Navigate to="/settings/domains" replace />} />
                <Route path="appearance" element={<AppearanceSettingsPage />} />
                <Route path="domains" element={<BusinessDomainsSettingsPage />} />
                <Route path="categories" element={<CategoriesSettingsPage />} />
                <Route path="statuses" element={<StatusSettingsPage />} />
                <Route path="tags" element={<TagsSettingsPage />} />
                <Route path="users" element={<UsersSettingsPage />} />
                <Route path="odoo" element={<OdooImportPage />} />
                <Route path="ai" element={<AISettingsPage />} />
                <Route path="accountability" element={<AccountabilityPage />} />
                <Route path="activity" element={<ActivityLogPage />} />
                {/* Admin routes - temporarily accessible to all users for debugging */}
                {/* TODO: Restore AdminRoute wrapper after fixing auth */}
                <Route path="admin" element={<AdminSettingsPage />} />
                <Route path="global-users" element={<GlobalUsersPage />} />
              </Route>

              {/* Profile */}
              <Route path="/profile" element={<ProfilePage />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </AuthInitializer>
          </ToastBridge>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
